/**
 * Turning a preflight/compile `code` into something a partner can read.
 *
 * `PreflightIssue.detail` and `CompileWarning.detail` are deliberately
 * developer-facing English (`"src" is required by core.image but has no value`) —
 * they name components, params and fragment keys, which is what makes them useful
 * in a bug report. They were also being rendered straight into the editor's alert,
 * so a German partner saw raw English naming internal identifiers.
 *
 * This maps the stable `code` to a translated sentence, and the UI keeps `detail`
 * as secondary technical text. The split is why no `compile.ts` change is needed:
 * the specifics stay in `detail` rather than being threaded through as message
 * arguments, which would mean restructuring every warning site and its tests.
 */
import { COMPILE_WARNING } from './compile';
import { PREFLIGHT } from './preflight';
import { RICH_TEXT_REJECTION, RichTextRejection } from '../richTextValue';

export interface IssueMessage {
  id: string;
  defaultMessage: string;
}

const MESSAGES: Record<string, IssueMessage> = {
  [PREFLIGHT.MISSING_FRAGMENT]: {
    id: 'pages.emailCenter.visual.issue.missingFragment',
    defaultMessage:
      'This email uses a building block that is not available here, so it would fail to send.',
  },
  [PREFLIGHT.MISSING_LAYOUT]: {
    id: 'pages.emailCenter.visual.issue.missingLayout',
    defaultMessage:
      'The layout this email uses is not available here, so it would fail to send.',
  },
  [PREFLIGHT.LAYOUT_NOT_A_LAYOUT]: {
    id: 'pages.emailCenter.visual.issue.layoutNotALayout',
    defaultMessage:
      'The selected layout is a building block, not a layout — the email would arrive empty.',
  },
  [PREFLIGHT.LAYOUT_SLOT_NOT_DECLARED]: {
    id: 'pages.emailCenter.visual.issue.layoutSlotNotDeclared',
    defaultMessage:
      'This email’s content does not fit anywhere in the selected layout, so it would arrive empty.',
  },
  [PREFLIGHT.DEFERRED_RENDER_TIME_VAR]: {
    id: 'pages.emailCenter.visual.issue.deferredRenderTimeVar',
    defaultMessage:
      'A design value sits inside a section that is left for the mail provider to fill in, so recipients would see the code for it instead of the value.',
  },
  [COMPILE_WARNING.UNSUPPORTED_BLOCK]: {
    id: 'pages.emailCenter.visual.issue.unsupportedBlock',
    defaultMessage: 'One block cannot be built, so it would be left out of the email.',
  },
  [COMPILE_WARNING.MISSING_REQUIRED_PROP]: {
    id: 'pages.emailCenter.visual.issue.missingRequiredProp',
    defaultMessage: 'A block is missing something it needs — check its settings.',
  },
  [COMPILE_WARNING.EMPTY_CONDITION]: {
    id: 'pages.emailCenter.visual.issue.emptyCondition',
    defaultMessage:
      'A block is set to show conditionally but has no conditions, so it always shows.',
  },
  [COMPILE_WARNING.MIXED_CONDITION_SCOPE]: {
    id: 'pages.emailCenter.visual.issue.mixedConditionScope',
    defaultMessage:
      'A block’s conditions mix recipient details with journey settings. Those are decided at different moments, so only the journey settings are used — split them into separate blocks.',
  },
  [COMPILE_WARNING.UNSERIALIZABLE_PROP]: {
    id: 'pages.emailCenter.visual.issue.unserializableProp',
    defaultMessage: 'One setting could not be applied and was left out.',
  },
  [COMPILE_WARNING.INLINE_UNSUPPORTED]: {
    id: 'pages.emailCenter.visual.issue.inlineUnsupported',
    defaultMessage:
      'This block asked to be built into the email directly; it is linked instead, which sends the same content.',
  },
};

/**
 * The message for an issue code, or null when the code has no translation yet.
 *
 * Null rather than a generic fallback so the caller can show the technical
 * `detail` on its own: a new code inherited from a future compiler warning is
 * better shown verbatim than mislabelled by a reassuring sentence.
 */
export function issueMessage(code: string): IssueMessage | null {
  return MESSAGES[code] ?? null;
}

/**
 * Why the formatting editor is not available for a value — as a sentence a partner can
 * act on, not a code.
 *
 * Each reason names the *thing in their content* that the editor cannot show, because
 * "this text contains layout markup" tells them where to look while "cannot edit here"
 * reads as a broken control.
 */
const RICH_TEXT_MESSAGES: Record<RichTextRejection, IssueMessage> = {
  [RICH_TEXT_REJECTION.TWIG_STATEMENT]: {
    id: 'pages.emailCenter.visual.richText.twigStatement',
    defaultMessage:
      'This text contains logic (like an if-condition), so it is edited as code to keep it intact.',
  },
  [RICH_TEXT_REJECTION.HTML_COMMENT]: {
    id: 'pages.emailCenter.visual.richText.htmlComment',
    defaultMessage:
      'This text contains comments that some email clients rely on, so it is edited as code to keep them intact.',
  },
  [RICH_TEXT_REJECTION.UNSUPPORTED_TAG]: {
    id: 'pages.emailCenter.visual.richText.unsupportedTag',
    defaultMessage:
      'This text contains its own layout markup, so it is edited as code rather than reformatted.',
  },
  [RICH_TEXT_REJECTION.UNSUPPORTED_ATTRIBUTE]: {
    id: 'pages.emailCenter.visual.richText.unsupportedAttribute',
    defaultMessage:
      'This text carries its own styling, so it is edited as code rather than reformatted.',
  },
  [RICH_TEXT_REJECTION.NOT_ROUND_TRIPPABLE]: {
    id: 'pages.emailCenter.visual.richText.notRoundTrippable',
    defaultMessage:
      'This text cannot be shown for formatting without changing it, so it is edited as code.',
  },
};

export function richTextRejectionMessage(
  rejection: RichTextRejection,
  intl: { formatMessage: (descriptor: IssueMessage) => string },
): string {
  return intl.formatMessage(RICH_TEXT_MESSAGES[rejection]);
}
